import { useState, useCallback, useRef, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useQuotaErrorSurface } from "@/hooks/useQuotaErrorSurface";

export interface LanguageReactResult {
  message: string;
  cached: boolean;
}

export interface LanguageReactParams {
  languageSessionId: string;
  nativeLang: "en" | "pt" | "nl";
  learningLang: string;
  lessonId: string;
}

export const useLanguageReact = () => {
  const [reaction, setReaction] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const generation = useRef(0);
  useEffect(() => () => { generation.current++; }, []);
  const surfaceQuota = useQuotaErrorSurface();

  const react = useCallback(async (params: LanguageReactParams) => {
    const ticket = ++generation.current;
    setIsLoading(true);
    setError(null);
    setReaction(null);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("not_authenticated");
      const { data, error: fnError } = await supabase.functions.invoke("language-react", {
        body: {
          language_session_id: params.languageSessionId,
          native_lang: params.nativeLang,
          learning_lang: params.learningLang,
          lesson_id: params.lessonId,
        },
      });
      if (ticket !== generation.current) return;
      if (fnError) {
        if (await surfaceQuota(fnError)) { setIsLoading(false); return; }
        throw fnError;
      }
      const result = data as LanguageReactResult;
      if (!result?.message) throw new Error("empty_response");
      if (ticket === generation.current) setReaction(result.message);
    } catch (err: any) {
      console.error("[useLanguageReact] failed", err);
      if (ticket === generation.current) { setError(err?.message || "unknown"); setReaction(null); }
    } finally {
      if (ticket === generation.current) setIsLoading(false);
    }
  }, [surfaceQuota]);

  const clear = useCallback(() => { generation.current++; setIsLoading(false); setReaction(null); setError(null); }, []);

  return { reaction, isLoading, error, react, clear };
};
